import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  ClipboardCheck,
  FileSearch,
  FlaskConical,
  KeyRound,
  Network,
  ShieldAlert,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { api } from '../lib/api';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

const QUICK_LINKS = [
  { to: '/internal/accounts', label: 'Accounts Overview', icon: Building2, description: 'Review masked workspace metadata, onboarding state, and recent activity summaries.' },
  { to: '/internal/identities', label: 'Identity Operations', icon: Users, description: 'Review internal identities and verification-linked records.' },
  { to: '/internal/digital-key-accounts', label: 'Digital Key Accounts', icon: KeyRound, description: 'Inspect linked digital key accounts, policy status, and recent login requests.' },
  { to: '/internal/digital-key-demo', label: 'Digital Key Demo Runner', icon: ClipboardCheck, description: 'Follow the internal runbook from identity to safe claims preview.' },
  { to: '/internal/relying-party-profiles', label: 'Relying Parties', icon: Network, description: 'Manage internal relying party profiles and statuses.' },
  { to: '/internal/fas-readiness', label: 'FAS Readiness', icon: ClipboardCheck, description: 'Track readiness notes for the internal FAS assessment path.' },
  { to: '/internal/bosa-evidence-pack', label: 'BOSA Evidence Pack', icon: FileSearch, description: 'Review internal evidence pack materials and references.' },
  { to: '/internal/audit-governance-pack', label: 'Audit Governance', icon: ShieldCheck, description: 'Open the internal audit and governance evidence pack.' },
  { to: '/internal/digital-key-architecture', label: 'Digital Key Layer', icon: KeyRound, description: 'Inspect the internal digital key architecture notes.' },
  { to: '/internal/authenticator-preview', label: 'Authenticator Preview', icon: FlaskConical, description: 'Preview the internal authenticator flow.' },
  { to: '/internal/oidc-lab', label: 'OIDC Lab', icon: Building2, description: 'Simulate the internal OIDC lab and login requests.' },
];

const OVERVIEW_CARDS = [
  {
    key: 'new_organizations',
    label: 'New organizations',
    detail: 'Created in the last 30 days.',
  },
  {
    key: 'onboarding_incomplete',
    label: 'Onboarding incomplete',
    detail: 'Organizations that still need workspace onboarding.',
  },
  {
    key: 'pending_verifications',
    label: 'Pending verifications',
    detail: 'Open verification sessions awaiting action.',
  },
  {
    key: 'recent_verification_activity',
    label: 'Recent verification activity',
    detail: 'Verification sessions touched in the last 30 days.',
  },
  {
    key: 'identity_operations',
    label: 'Identity operations',
    detail: 'Total internal idem identity records.',
  },
  {
    key: 'fas_bosa_readiness',
    label: 'FAS/BOSA readiness',
    detail: 'High-readiness internal identities.',
  },
];

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function normalizeLabel(value) {
  return String(value || '—').replaceAll('_', ' ');
}

function OverviewCard({ label, value, detail }) {
  return (
    <section className="border border-zinc-200 bg-white p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="mt-2 font-display text-2xl font-semibold tracking-tight text-zinc-900">{value}</p>
      <p className="mt-2 text-sm leading-6 text-zinc-600">{detail}</p>
    </section>
  );
}

function QuickLinkCard({ to, label, icon: Icon, description }) {
  return (
    <Link to={to} className="group border border-zinc-200 bg-white p-4 transition-colors hover:border-zinc-400">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center border border-zinc-200 bg-zinc-50 text-zinc-700 transition-colors group-hover:border-zinc-300">
          <Icon className="h-4 w-4" strokeWidth={1.5} />
        </span>
        <div className="min-w-0">
          <p className="font-medium text-zinc-900">{label}</p>
          <p className="mt-1 text-sm leading-6 text-zinc-600">{description}</p>
        </div>
      </div>
    </Link>
  );
}

function ActivityLink({ item }) {
  if (!item.detail_href) return <span className="text-zinc-400">—</span>;
  return (
    <Link to={item.detail_href} className="text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:text-zinc-900 hover:decoration-zinc-900">
      Open identity
    </Link>
  );
}

export default function InternalDashboard() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get('/admin/internal-overview');
        if (!cancelled) setOverview(data);
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError?.response?.data?.error || 'Internal overview could not be loaded.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const counts = overview?.counts || {};
  const recentActivity = overview?.recent_activity || [];

  return (
    <div data-testid="internal-dashboard-page" className="space-y-6">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="Overview" showDashboard={false} />

      <section className="border border-amber-200 bg-amber-50 p-5 text-amber-900">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={1.5} />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-amber-700">Internal admin</p>
            <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-amber-950">Platform operations, digital key readiness, and onboarding monitoring.</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6">
              This area is reserved for platform-admin workflows only. Customer workspace data stays in the normal dashboard and
              workspace APIs. No raw evidence, keys, secrets, or cross-tenant admin surface is exposed from this page.
            </p>
          </div>
        </div>
      </section>

      <section className="border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        <p className="font-medium">Internal only</p>
        <p className="mt-1">
          Treat everything here as platform-ops tooling. Do not use this portal for customer workspace actions or expose its
          data outside trusted internal staff.
        </p>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" aria-label="Internal overview">
        {OVERVIEW_CARDS.map((card) => (
          <OverviewCard
            key={card.key}
            label={card.label}
            value={loading ? '…' : counts[card.key] ?? 0}
            detail={card.detail}
          />
        ))}
      </section>

      <section className="border border-zinc-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Recent onboarding &amp; verification activity</p>
            <h2 className="mt-1 font-display text-xl font-semibold tracking-tight">Internal operations timeline</h2>
          </div>
          <Link to="/internal/identities" className="text-sm text-zinc-600 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">
            Open Identity Operations
          </Link>
        </div>

        {loading ? (
          <p className="mt-4 text-sm text-zinc-500">Loading internal overview…</p>
        ) : error ? (
          <p className="mt-4 text-sm text-red-700">{error}</p>
        ) : recentActivity.length ? (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="py-2 pr-4 font-medium">Organization</th>
                  <th className="py-2 pr-4 font-medium">Type / method</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Last updated</th>
                  <th className="py-2 font-medium">Detail</th>
                </tr>
              </thead>
              <tbody>
                {recentActivity.map((item) => (
                  <tr key={`${item.kind}-${item.organization_id}-${item.verification_id || item.last_updated_at}`} className="border-b border-zinc-100 last:border-b-0 align-top">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-zinc-900">{item.organization_name}</div>
                      <div className="mt-0.5 text-xs text-zinc-500">{item.kind === 'organization' ? 'Onboarding record' : `Org ${item.organization_id}`}</div>
                    </td>
                    <td className="py-3 pr-4 text-zinc-600">{normalizeLabel(item.type_method)}</td>
                    <td className="py-3 pr-4 text-zinc-600">{normalizeLabel(item.status)}</td>
                    <td className="py-3 pr-4 text-zinc-600">{formatDate(item.last_updated_at)}</td>
                    <td className="py-3 text-zinc-600"><ActivityLink item={item} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-4 text-sm text-zinc-500">No recent onboarding or verification activity in the current window.</p>
        )}
      </section>

      <section className="border border-zinc-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Quick links</p>
            <h2 className="mt-1 font-display text-xl font-semibold tracking-tight">Internal surfaces</h2>
          </div>
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {QUICK_LINKS.map((link) => (
            <QuickLinkCard key={link.to} {...link} />
          ))}
        </div>
      </section>
    </div>
  );
}
